"use client";

import * as React from "react";
import { Dialog as D } from "radix-ui";
import { Command } from "cmdk";
import {
  Building2,
  Car,
  FileText,
  Flag,
  Keyboard,
  Moon,
  Route,
  Search,
  Truck,
} from "lucide-react";
import type { ResultadoBusca } from "@/types";
import { openScreen } from "@/core/tabs";
import { usePrefs } from "@/core/prefs";
import { can, useSession } from "@/core/session";
import { SCREEN_META, type ScreenKey } from "@/core/screen-meta";
import { api } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { Kbd } from "@/components/ui/controls";
import { MENUS, SHORTCUTS } from "./menus";
import { useUi } from "./ui-store";

const itemCls =
  "flex h-8 cursor-default items-center gap-2.5 rounded-sm px-2 text-sm data-[selected=true]:bg-primary-soft [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted";
const groupCls =
  "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-subtle";

const ICONE_BUSCA = {
  cte: FileText,
  viagem: Route,
  veiculo: Car,
  tomador: Building2,
  mdfe: Truck,
} as const;

export function CommandPalette() {
  const open = useUi((s) => s.palette);
  const setOpen = useUi((s) => s.setPalette);
  const papel = useSession((s) => s.usuario?.papel);
  const [q, setQ] = React.useState("");
  const [resultados, setResultados] = React.useState<ResultadoBusca[]>([]);
  const [buscando, setBuscando] = React.useState(false);

  React.useEffect(() => {
    const termo = q.trim();
    if (termo.length < 2) return;
    let alive = true;
    const t = setTimeout(() => {
      setBuscando(true);
      api<ResultadoBusca[]>("/busca", { query: { q: termo } })
        .then((r) => alive && setResultados(r))
        .catch(() => alive && setResultados([]))
        .finally(() => alive && setBuscando(false));
    }, 220);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q]);

  const fechar = () => {
    setOpen(false);
    setQ("");
    setResultados([]);
  };
  const ir = (screen: ScreenKey, params?: Record<string, string>) => {
    fechar();
    openScreen(screen, params);
  };

  const telas = React.useMemo(() => {
    const seen = new Set<string>();
    const out: {
      screen: ScreenKey;
      label: string;
      params?: Record<string, string>;
      grupo: string;
    }[] = [{ screen: "inicio", label: "Início", grupo: "" }];
    for (const m of MENUS)
      for (const it of m.items) {
        if (!it.screen) continue;
        const k = it.label;
        if (seen.has(k)) continue;
        seen.add(k);
        const acao = SCREEN_META[it.screen].acao;
        if (acao && !can(papel, acao)) continue;
        out.push({
          screen: it.screen,
          label: it.label,
          params: it.params,
          grupo: m.label,
        });
      }
    out.push(
      { screen: "notificacoes", label: "Notificações", grupo: "" },
      { screen: "perfil", label: "Perfil", grupo: "" },
    );
    return out;
  }, [papel]);

  const mostrarResultados = q.trim().length >= 2;

  return (
    <D.Root open={open} onOpenChange={(v) => (v ? setOpen(true) : fechar())}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-overlay animate-in" />
        <D.Content
          aria-describedby={undefined}
          className="fixed top-[12%] left-1/2 z-50 w-[600px] max-w-[calc(100vw-32px)] -translate-x-1/2 overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-popover outline-none animate-in"
        >
          <D.Title className="sr-only">Buscar</D.Title>
          <Command loop shouldFilter>
            <div className="flex items-center gap-2 border-b border-border px-3">
              <Search className="size-4 text-subtle" />
              <Command.Input
                value={q}
                onValueChange={(v) => {
                  setQ(v);
                  if (v.trim().length < 2) setResultados([]);
                }}
                autoFocus
                placeholder="Telas, ações, CT-e, chave, placa, tomador"
                className="h-11 flex-1 bg-transparent text-sm outline-none"
              />
              {buscando ? (
                <Spinner className="size-4 text-subtle" />
              ) : (
                <Kbd>Esc</Kbd>
              )}
            </div>
            <Command.List className="max-h-[420px] overflow-auto p-1.5">
              <Command.Empty className="px-2 py-6 text-center text-sm text-muted">
                {buscando ? "Buscando" : "Nada encontrado"}
              </Command.Empty>

              {mostrarResultados && resultados.length ? (
                <Command.Group heading="Registros" className={groupCls}>
                  {resultados.map((r) => {
                    const Icon = ICONE_BUSCA[r.tipo];
                    return (
                      <Command.Item
                        key={`${r.tipo}-${r.id}`}
                        value={`${r.titulo} ${r.subtitulo} ${q}`}
                        onSelect={() => {
                          const num = r.titulo.replace(/\D/g, "");
                          if (r.tipo === "cte")
                            ir("cte", { id: r.id, numero: num });
                          else if (r.tipo === "viagem")
                            ir("viagem", { id: r.id, numero: num });
                          else if (r.tipo === "mdfe")
                            ir("mdfe", {
                              id: r.id,
                              numero: r.titulo.replace(/\D/g, ""),
                            });
                          else if (r.tipo === "veiculo")
                            ir("veiculo", { id: r.id, nome: r.titulo });
                          else ir("tomador", { id: r.id, nome: r.titulo });
                        }}
                        className={itemCls}
                      >
                        <Icon />
                        <span className="truncate">{r.titulo}</span>
                        <span className="ml-auto truncate text-xs text-subtle">
                          {r.subtitulo}
                        </span>
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              ) : null}

              <Command.Group heading="Ações" className={groupCls}>
                {can(papel, "viagem.emitir") ? (
                  <Command.Item
                    value="Nova viagem emitir"
                    onSelect={() => ir("nova-viagem")}
                    className={itemCls}
                  >
                    <Route />
                    Nova viagem
                    <Kbd className="ml-auto">F2</Kbd>
                  </Command.Item>
                ) : null}
                <Command.Item
                  value="Encerrar MDF-e viagem"
                  onSelect={() => {
                    fechar();
                    useUi.getState().setEncerrar(true);
                  }}
                  className={itemCls}
                >
                  <Flag />
                  Encerrar MDF-e…
                </Command.Item>
                <Command.Item
                  value="Alternar tema claro escuro"
                  onSelect={() => {
                    const t = usePrefs.getState().theme;
                    usePrefs
                      .getState()
                      .setTheme(t === "dark" ? "light" : "dark");
                    fechar();
                  }}
                  className={itemCls}
                >
                  <Moon />
                  Alternar tema
                </Command.Item>
                <Command.Item
                  value="Mapa de atalhos teclado"
                  onSelect={() => {
                    fechar();
                    useUi.getState().setShortcuts(true);
                  }}
                  className={itemCls}
                >
                  <Keyboard />
                  Mapa de atalhos
                  <Kbd className="ml-auto">?</Kbd>
                </Command.Item>
                
              </Command.Group>

              <Command.Group heading="Telas" className={groupCls}>
                {telas.map((t) => {
                  const Icon = SCREEN_META[t.screen].icon;
                  return (
                    <Command.Item
                      key={`${t.screen}-${t.label}`}
                      value={`${t.label} ${t.grupo}`}
                      onSelect={() => ir(t.screen, t.params)}
                      className={itemCls}
                    >
                      <Icon />
                      {t.label}
                      {t.grupo ? (
                        <span className="ml-auto text-xs text-subtle">
                          {t.grupo}
                        </span>
                      ) : null}
                    </Command.Item>
                  );
                })}
              </Command.Group>
            </Command.List>
          </Command>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

export function ShortcutsDialog() {
  const open = useUi((s) => s.shortcuts);
  const setOpen = useUi((s) => s.setShortcuts);
  return (
    <D.Root open={open} onOpenChange={setOpen}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-overlay animate-in" />
        <D.Content
          aria-describedby={undefined}
          className="fixed top-[16%] left-1/2 z-50 w-[420px] max-w-[calc(100vw-32px)] -translate-x-1/2 rounded-lg border border-border bg-surface text-fg shadow-popover outline-none animate-in"
        >
          <div className="flex h-10 items-center border-b border-border px-4">
            <D.Title className="text-base font-semibold">Atalhos</D.Title>
          </div>
          <ul className="p-2">
            {SHORTCUTS.map((s) => (
              <li
                key={s.label}
                className="flex h-8 items-center justify-between px-2 text-sm"
              >
                <span>{s.label}</span>
                <span className="flex gap-1">
                  {s.keys.split(" · ").map((k) => (
                    <Kbd key={k}>{k}</Kbd>
                  ))}
                </span>
              </li>
            ))}
          </ul>
          <p className="border-t border-border px-4 py-2 text-xs text-subtle">
            Ctrl+W e Ctrl+Tab podem ser reservados pelo navegador; use Alt+W e
            Alt+→.
          </p>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
